<template>
  <v-card elevation="1" height="270">
    <v-card-title class="text-h5">
      {{ $t('performance_dashboard_ui.year_over_year_trend') }}
      <info-modal width="800">
        <v-card-title class="text-h5">
          {{ $t('performance_dashboard_ui.modal_info.temperature_violations.yoy_trend.title') }}
        </v-card-title>
        <v-card-text v-html="$t('performance_dashboard_ui.modal_info.temperature_violations.yoy_trend.text')"/>
      </info-modal>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <div class="text-subtitle-1" v-if="prevYearTotalRoutes === 0 || currYearTotalRoutes === 0">
        {{ $t('no_data_available') }}
      </div>
      <div v-else class="d-flex" ref="container">
        <div ref="graph" class="flex-grow-1">
          <div class="d-flex align-items-end justify-content-start graph-container pt-5" :style="`height: ${height}px`">
            <div class="graph-bar-prev" :style="`height: ${floor(prevYearPercent, 0)}%`">
              <div class="mt-n6 text-truncate">{{ prevYearPercent }}%</div>
            </div>
            <div class="graph-bar-curr" :style="`height: ${floor(currYearPercent, 0)}%`">
              <div class="mt-n6 text-truncate">{{ currYearPercent }}%</div>
            </div>
          </div>
          <div class="d-flex align-items-end justify-content-start graph-labels">
            <div class="graph-label-prev text-truncate">{{ prevYearLabel }}</div>
            <div class="graph-label-curr text-truncate">{{ currYearLabel }}</div>
          </div>
        </div>
        <div class="align-self-center yoy-change flex-grow-0">
          <v-avatar color="grey darken-2">
            <v-icon v-if="percentageChange > 0" dark large>mdi-arrow-up</v-icon>
            <v-icon v-else-if="percentageChange < 0" dark large>mdi-arrow-down</v-icon>
            <v-icon v-else dark large>mdi-equal</v-icon>
          </v-avatar>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import MathUtil from '@/util/MathUtil'
import { sumBy, values, floor, round } from 'lodash'

export default {
  name: 'TemperatureViolationsYearOverYear',
  components: {
    InfoModal
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    height: {
      type: Number,
      default: 200
    },
    requestedCustomerIds: {
      type: Array
    }
  },
  data () {
    return {
      loaded: false,
      prevYearData: null,
      currYearData: null
    }
  },
  methods: {
    floor: floor,
    /**
     * Load the data from the API.
     */
    async loadData () {
      this.loaded = false
      const requestedCustomerIds = this.requestedCustomerIds.join(',')

      const currYearStart =
        Moment.tz(this.customerTimezone)
          .startOf('month')
          .subtract(1, 'month')

      const currYearEnd = currYearStart.clone().endOf('month')
      const prevYearStart = currYearStart.clone().subtract(1, 'year')
      const prevYearEnd = currYearEnd.clone().subtract(1, 'year')

      try {
        let response = await this.$httpAdapter.get('/customer-insights/temperature-violation-stats', {
          params: {
            customer_id: requestedCustomerIds,
            start: prevYearStart.format(),
            end: prevYearEnd.format()
          }
        })
        this.prevYearData = response.data

        response = await this.$httpAdapter.get('/customer-insights/temperature-violation-stats', {
          params: {
            customer_id: requestedCustomerIds,
            start: currYearStart.format(),
            end: currYearEnd.format()
          }
        })
        this.currYearData = response.data
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      }
    }
  },
  computed: {
    prevYearLabel () {
      return Moment.tz(this.customerTimezone)
        .startOf('month')
        .subtract(1, 'month')
        .subtract(1, 'year')
        .format('MMM YYYY')
    },
    currYearLabel () {
      return Moment.tz(this.customerTimezone)
        .startOf('month')
        .subtract(1, 'month')
        .format('MMM YYYY')
    },
    prevYearTotalRoutes () {
      return sumBy(values(this.prevYearData), 'num_total_routes')
    },
    prevYearInSpec () {
      return sumBy(values(this.prevYearData), 'num_in_spec_routes')
    },
    prevYearPercent () {
      return MathUtil.percentage(this.prevYearInSpec, this.prevYearTotalRoutes, 1)
    },
    currYearTotalRoutes () {
      return sumBy(values(this.currYearData), 'num_total_routes')
    },
    currYearInSpec () {
      return sumBy(values(this.currYearData), 'num_in_spec_routes')
    },
    currYearPercent () {
      return MathUtil.percentage(this.currYearInSpec, this.currYearTotalRoutes, 1)
    },
    percentageChange () {
      return round(this.currYearPercent - this.prevYearPercent, 1)
    },
    percentageColor () {
      if (this.percentageChange < 0) {
        return 'danger'
      } else if (this.percentageChange > 0) {
        return 'success'
      } else {
        return 'blue-grey'
      }
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>

<style scoped lang="scss">

.graph-container {
  gap: 20%;
  text-align: center;
}
.graph-labels {
  gap: 20%;
  font-size: .75rem;
  text-align: center;
}
.yoy-change {
  width: 50px;
  padding-right: 15%;
}
.graph-bar-prev {
  width: 25%;
  max-width: 100px;
  margin-left: 10%;
  background-color: #195152;
  opacity: .8;
}
.graph-label-prev {
  text-align: center;
  width: 25%;
  max-width: 100px;
  margin-left: 10%;
}
.graph-bar-curr {
  width: 25%;
  max-width: 100px;
  background-color: #195152;
}
.graph-label-curr {
  text-align: center;
  width: 25%;
  max-width: 100px;
}
</style>
